import React, { useState } from "react";
import {
    Box,
    Slider,
    Typography,
    TextField,
    Card,
    CardContent,
    IconButton
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack, ArrowForward } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    lessonTitle: {
        marginBottom: theme.spacing(2),
        backgroundColor: "#F17632",
        borderRadius: "10px",
        color: "#fff",
        width: "100%",
        padding: "15px 4px",
        textAlign: "center",
    },
    questionContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    },
    questionCard: {
        width: "100%",
        maxWidth: "800px",
        minWidth: "800px",
        minHeight: "200px",
        padding: theme.spacing(2),
        margin: theme.spacing(2),
        backgroundColor: '#F3F1EC !important',
        border: '2px dotted #0097a7',
        borderRadius:'30px !important',
    },
    navButton: {
        backgroundColor: "#F17632 !important",
        color: "#fff !important",
        "&:hover": {
            backgroundColor: "#D65F2B !important",
        },
    },
    prevButton: {
        marginRight: theme.spacing(2),
    },
    nextButton: {
        marginLeft: theme.spacing(2),
    },
    disabledButton: {
        backgroundColor: "#ccc !important",
    },
}));

const questions = [
    {
        AssessmentID: "c1_mod1_unit1_l2_MCQ_1",
        AssessmentType: "MCQ",
        AssessmentQuestion: "How does multitasking primarily affect attention span compared to focusing on a single task?",
        AnswerOptions: [
            { OptionID: "opt1", OptionText: "It significantly decreases attention span." },
            { OptionID: "opt2", OptionText: "It significantly increases attention span." },
            { OptionID: "opt3", OptionText: "It has no impact on attention span." },
            { OptionID: "opt4", OptionText: "It varies depending on the task." },
        ],
        CorrectAnswer: "opt1",
    },
    {
        AssessmentID: "c1_mod1_unit1_l1_assessment_2",
        AssessmentType: "TF",
        AssessmentQuestion: "True or False: Multitasking always improves productivity.",
        AnswerOptions: [
            { OptionID: "opt1", OptionText: "True" },
            { OptionID: "opt2", OptionText: "False" },
        ],
        CorrectAnswer: "opt2",
    },
    {
        AssessmentID: "c1_mod1_unit1_l1_assessment_3",
        AssessmentType: "MAQ",
        AssessmentQuestion: "Which of the following is a consequence of multitasking on cognitive performance?",
        AnswerOptions: [
            { OptionID: "opt1", OptionText: "Decreased efficiency." },
            { OptionID: "opt2", OptionText: "Increased error rates." },
            { OptionID: "opt3", OptionText: "Enhanced focus." },
            { OptionID: "opt4", OptionText: "Better memory retention." },
        ],
        CorrectAnswer: ["opt1", "opt2"],
    },
    {
        AssessmentID: "c1_mod1_unit1_l1_assessment_4",
        AssessmentType: "Theory",
        AssessmentQuestion: "Explain the importance of shifting perspectives in addressing AI-related challenges.",
        AnswerOptions: [],
        CorrectAnswer: null,
    },
];

const ProgramAssessment = ({ selectedLesson }) => {
    const classes = useStyles();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    };

    const handleAnswerChange = (questionId, value) => {
        setAnswers({ ...answers, [questionId]: value });
    };

    const isNextDisabled = () => {
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion.AssessmentType === "Theory") {
            return !answers[currentQuestion.AssessmentID] || answers[currentQuestion.AssessmentID].trim() === "";
        }
        return !answers[currentQuestion.AssessmentID];
    };

    const renderQuestion = () => {
        const currentQuestion = questions[currentQuestionIndex];

        const handleOptionSelect = (questionId, optionId) => {
            setAnswers({ ...answers, [questionId]: optionId });
        };

        const handleMultiSelectChange = (questionId, optionId) => {
            const selectedOptions = answers[questionId] || [];
            setAnswers({
                ...answers,
                [questionId]: selectedOptions.includes(optionId)
                    ? selectedOptions.filter((opt) => opt !== optionId)
                    : [...selectedOptions, optionId],
            });
        };

        switch (currentQuestion.AssessmentType) {
            case "MCQ":
            case "TF":
                return (
                    <Box>
                        {currentQuestion.AnswerOptions.map((option) => (
                            <Card
                                key={option.OptionID}
                                onClick={() => handleOptionSelect(currentQuestion.AssessmentID, option.OptionID)}
                                sx={{
                                    marginBottom: 1,
                                    padding: 2,
                                    cursor: "pointer",
                                    backgroundColor: answers[currentQuestion.AssessmentID] === option.OptionID
                                        ? "#F17632"
                                        : "#f5f5f5",
                                    color: answers[currentQuestion.AssessmentID] === option.OptionID
                                        ? "#fff"
                                        : "#000",
                                    borderRadius: "10px",
                                    transition: "0.3s",
                                }}
                            >
                                <Typography>{option.OptionText}</Typography>
                            </Card>
                        ))}
                    </Box>
                );

            case "MAQ":
                return (
                    <Box>
                        {currentQuestion.AnswerOptions.map((option) => (
                            <Card
                                key={option.OptionID}
                                onClick={() => handleMultiSelectChange(currentQuestion.AssessmentID, option.OptionID)}
                                sx={{
                                    marginBottom: 1,
                                    padding: 2,
                                    cursor: "pointer",
                                    backgroundColor: answers[currentQuestion.AssessmentID]?.includes(option.OptionID)
                                        ? "#F17632"
                                        : "#f5f5f5",
                                    color: answers[currentQuestion.AssessmentID]?.includes(option.OptionID)
                                        ? "#fff"
                                        : "#000",
                                    borderRadius: "10px",
                                    transition: "0.3s",
                                }}
                            >
                                <Typography>{option.OptionText}</Typography>
                            </Card>
                        ))}
                    </Box>
                );

            case "Theory":
                return (
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        placeholder="Write your answer here..."
                        value={answers[currentQuestion.AssessmentID] || ""}
                        onChange={(e) =>
                            handleAnswerChange(currentQuestion.AssessmentID, e.target.value)
                        }
                    />
                );

            default:
                return <Typography>No question available</Typography>;
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="h5" className={classes.lessonTitle}>
                {selectedLesson.title}
            </Typography>

            <div style={{ backgroundColor: '#F3F1EC', padding: '20px', width: '100%', position: 'relative', marginTop: '40px', border: '2px dotted #0097a7', borderRadius:'30px', }}>
                <Typography variant="body1" style={{marginTop: '10px', textAlign:'left', color: '#525252'}}>
                    This section contains a list of questions designed to assess the learner’s prior knowledge, attitudes, and skills. You can unselect or switch each question based on your specific needs. This assessment helps you understand where your students stand before the lesson begins, allowing you to tailor your teaching approach accordingly.
                </Typography>
            </div>

            {/* Progress Slider */}
            <Box sx={{ width: "88%", mt: 2, color: "#F17632" }}>
                <Slider
                    value={questions.length > 1 ? (currentQuestionIndex / (questions.length - 1)) * 100 : 0}
                    disabled
                    sx={{ color: "#F17632 !important" }}
                />
            </Box>

            {/* Question Card with Navigation Arrows */}
            <Box className={classes.questionContainer}>
                <IconButton
                    onClick={handlePrev}
                    disabled={currentQuestionIndex === 0}
                    className={`${classes.navButton} ${currentQuestionIndex === 0 ? classes.disabledButton : ""}`}
                >
                    <ArrowBack />
                </IconButton>

                <Card className={classes.questionCard}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom pb={3} color="#0397A7">
                            {questions[currentQuestionIndex]?.AssessmentQuestion || "Loading..."}
                        </Typography>
                        {renderQuestion()}
                    </CardContent>
                </Card>

                <IconButton
                    onClick={handleNext}
                    disabled={isNextDisabled()}
                    className={`${classes.navButton} ${isNextDisabled() ? classes.disabledButton : ""}`}
                >
                    <ArrowForward />
                </IconButton>
            </Box>
        </Box>
    );
};

export default ProgramAssessment;
